import {Injectable} from '@angular/core';
import {Observable, Subject, Subscription} from 'rxjs';
import {DigitalPlanValidationStatusResponse} from './digital-plan.model';
import {DigitalPlanService} from './digital-plan.service';
import {PlanModel} from '../common/service/plan-model.service';

/**
 * Holds the digital plan validation the Digital Plan tab displays, together with the poll subscription driving it.
 *
 * Provided at the {@link DigitalPlanComponent} level (not root) so each tab gets its own instance that is destroyed with
 * the component and never leaks across plans. The same instance is shared with the hamburger, so a validation the
 * hamburger triggers while progressing to Ready for Certification updates the same displayed state and reuses the same
 * poll rather than opening a second one.
 */
@Injectable()
export class DigitalPlanValidationState {
    private static readonly LOG_PREFIX = 'DigitalPlanValidationState';

    validation: DigitalPlanValidationStatusResponse;

    errorMessage: string;

    private subscription: Subscription;

    constructor(private readonly digitalPlanService: DigitalPlanService) {}

    /**
     * Runs the validation, owning the poll subscription. Returns an Observable that emits the terminal result once and
     * then completes, so a caller (e.g. the hamburger gating a transition) can react to the outcome without opening a
     * second poll against the same run.
     *
     * @param reuse passed through to the backend; see {@link DigitalPlanService.runValidation}.
     */
    validate(reuse = false): Observable<DigitalPlanValidationStatusResponse> {
        console.debug(`${DigitalPlanValidationState.LOG_PREFIX} - validate - reuse=${reuse}`);

        // Bridge the single poll to any external caller so the tab display and the caller's gating logic share one run.
        const result = new Subject<DigitalPlanValidationStatusResponse>();

        this.stopPolling();
        this.errorMessage = undefined;
        this.subscription = this.digitalPlanService.runValidation(reuse).subscribe({
            next: value => (this.validation = value),
            error: err => {
                console.error(`${DigitalPlanValidationState.LOG_PREFIX} - validate - error:`, err);
                this.errorMessage = err.error;
                result.error(err);
            },
            complete: () => {
                result.next(this.validation);
                result.complete();
            }
        });

        return result.asObservable();
    }

    /** Loads any existing (possibly in-progress) validation for the current plan, replacing any displayed state. */
    load(): void {
        this.clear();
        this.subscription = this.digitalPlanService.getValidation().subscribe({
            // Only display validations that are not cancelled
            next: value => (this.validation = value && value.status !== 'Cancelled' ? value : null),
            error: err => {
                console.error(`${DigitalPlanValidationState.LOG_PREFIX} - load - error:`, err);
                this.errorMessage = err.error;
            }
        });
    }

    /** Stops the poll and drops the displayed validation and error. */
    clear(): void {
        this.stopPolling();
        this.validation = null;
        this.errorMessage = undefined;
    }

    /** Stops the poll without clearing the displayed validation. */
    stopPolling(): void {
        this.subscription?.unsubscribe();
        this.subscription = null;
    }

    /**
     * Derives the plan's lot counts from the current validation and writes them onto the given model. This is the single
     * source of truth for planLots/chargeablePlanLots; the Digital Plan tab calls it during change detection to keep the
     * displayed counts live, and the hamburger calls it just before persisting the stage transition so the counts saved
     * reflect the completed validation rather than whatever the last in-progress poll happened to leave on the model.
     */
    applyLotCounts(planModel: PlanModel): void {
        const planPurpose = planModel.planPurpose;
        if (!planPurpose || planPurpose.maxNumberLotsCreated === 0) {
            planModel.planLots = 0;
            planModel.chargeablePlanLots = 0;
            return;
        }

        planModel.planLots = this.validation?.csdLotCount || 0;
        if (planModel.lodgementType?.value === 'Amendment') {
            planModel.chargeablePlanLots = 0;
        } else {
            planModel.chargeablePlanLots = (this.validation?.csdChargeableLotCount || 0) + (planModel.numberOfInsetLots || 0);
        }
    }
}
